import { inject, type InjectionKey } from 'vue'

import {
  type FrappeAppInstance,
  frappeAppInstanceKey,
  type FrappeAuthInstance,
  frappeAuthInstanceKey,
  type FrappeAuthRequest,
  type FrappeAuthResponse,
  type FrappeCallInstance,
  frappeCallInstanceKey,
  type FrappeDataInstance,
  frappeDataInstanceKey,
  type FrappeFileInstance,
  frappeFileInstanceKey,
  type FrappeInstanceError,
  isFrappeInstanceError,
} from '@/plugins/vuePluginFrappe'
import { requireExistance } from '@/utilities/typeguards'

import { CustomError, handleUnknownError } from './errors'

type TransformErrorObjectFunction = <ReturnType>(
  frappeFunction: () => Promise<ReturnType>,
) => never | Promise<ReturnType>

interface NetworkErrorCauseObject extends Error {
  // optional attribute needed for creating error instance without this information
  httpErrorCode?: number
  // optional attribute needed for creating error instance without this information
  httpErrorCodeText?: string
  httpErrorType?: string | undefined
}

const networkErrorName = 'NetworkError'

class NetworkError extends CustomError<NetworkErrorCauseObject> {
  public constructor(networkErrorMessage?: string, networkErrorCause?: NetworkErrorCauseObject) {
    super(networkErrorName, networkErrorMessage, networkErrorCause)
  }
}

const injectFrappePlugin = <PluginFrappeInstanceType>(
  frappeInstanceKey: InjectionKey<PluginFrappeInstanceType>,
): PluginFrappeInstanceType => {
  const possibleFrappeInstance = inject(frappeInstanceKey)
  const errorFallbackMessage = 'missing frappe instance key description'

  // frappe instance existance check (assertion function)
  requireExistance(possibleFrappeInstance, frappeInstanceKey.description ?? errorFallbackMessage)
  // afterwards existance of frappe instance (see type definition)
  const frappeInstance: NonNullable<PluginFrappeInstanceType> = possibleFrappeInstance

  return frappeInstance
}

const getFrappeAppInstance = (): FrappeAppInstance => {
  return injectFrappePlugin(frappeAppInstanceKey)
}
const getFrappeAuthInstance = (): FrappeAuthInstance => {
  return injectFrappePlugin(frappeAuthInstanceKey)
}
const getFrappeCallInstance = (): FrappeCallInstance => {
  return injectFrappePlugin(frappeCallInstanceKey)
}
const getFrappeDataInstance = (): FrappeDataInstance => {
  return injectFrappePlugin(frappeDataInstanceKey)
}
const getFrappeFileInstance = (): FrappeFileInstance => {
  return injectFrappePlugin(frappeFileInstanceKey)
}

const networkErrorCodeNoPermission = 403
const networkErrorCodeServiceInternal = 500
const networkErrorCodeSessionTimeOut = 401
const networkErrorCodeWrongRequest = 417
const networkErrorCodeExpectationAuthList = [
  networkErrorCodeNoPermission,
  networkErrorCodeSessionTimeOut,
]
const networkErrorCodeExpectationEditList = [
  networkErrorCodeServiceInternal,
  networkErrorCodeWrongRequest,
]

const isNetworkErrorRedirectContentPage = (communicationError: NetworkError): boolean => {
  let isRedirectError = false
  const authNetworkErrorCodeMissingFallback = 1

  if (communicationError.cause) {
    const currentNetworkErrorCode =
      communicationError.cause.httpErrorCode ?? authNetworkErrorCodeMissingFallback

    if (
      networkErrorCodeExpectationAuthList.includes(currentNetworkErrorCode) &&
      currentNetworkErrorCode !== authNetworkErrorCodeMissingFallback
    ) {
      isRedirectError = true
    }
  }

  return isRedirectError
}

const isNetworkErrorEditRedirectErrorPage = (communicationError: NetworkError): boolean => {
  let isRedirectError = false
  const editNetworkErrorCodeMissingFallback = 1

  if (communicationError.cause) {
    const currentNetworkErrorCode =
      communicationError.cause.httpErrorCode ?? editNetworkErrorCodeMissingFallback

    if (
      !networkErrorCodeExpectationEditList.includes(currentNetworkErrorCode) ||
      currentNetworkErrorCode === editNetworkErrorCodeMissingFallback
    ) {
      isRedirectError = true
    }
  }

  return isRedirectError
}

// transform objects from frappe framework returned during error states to correct error objects (instance of error)
const transformFrappeErrorObject: TransformErrorObjectFunction = async (frappeFunction) => {
  const functionReturnValue = await frappeFunction().catch((frappeFunctionError: unknown) => {
    if (isFrappeInstanceError(frappeFunctionError)) {
      const frappeInstanceError: FrappeInstanceError = frappeFunctionError
      const networkErrorCauseName = 'CauseNetworkError'
      // create error instance for correct error cause handling
      const { exception, message } = frappeInstanceError
      // Extract the actual exception message without the exception type prefix
      const exceptionMessage = exception.split(':').slice(1).join(':').trim()
      const normalizedMessage =
        // eslint-disable-next-line @typescript-eslint/prefer-optional-chain
        message && message.toLowerCase().startsWith('there was an error')
          ? exceptionMessage
          : message
      const networkErrorCauseInstance: NetworkErrorCauseObject = new Error(
        normalizedMessage || exceptionMessage,
      )

      // add additional error information to the custom error instance
      networkErrorCauseInstance.name = networkErrorCauseName
      networkErrorCauseInstance.httpErrorCode = frappeInstanceError.httpStatus
      networkErrorCauseInstance.httpErrorCodeText = frappeInstanceError.httpStatusText
      networkErrorCauseInstance.httpErrorType = frappeInstanceError.exc_type

      // transform to correct 401 http error
      if (networkErrorCauseInstance.httpErrorCode === networkErrorCodeNoPermission) {
        // currently frappe framework set "session_expired" attribute to this value (or not exist)
        const sessionExpireValue = 1

        if (frappeInstanceError.session_expired === sessionExpireValue) {
          networkErrorCauseInstance.httpErrorCode = networkErrorCodeSessionTimeOut
          networkErrorCauseInstance.httpErrorCodeText = 'UNAUTHORIZED'
          networkErrorCauseInstance.httpErrorType = 'AuthorizationError'

          console.debug(
            'transform authorization error: ',
            networkErrorCauseInstance.httpErrorCode,
            networkErrorCauseInstance.httpErrorCodeText,
            networkErrorCauseInstance.httpErrorType,
          )
        }
      }

      const networkErrorMessage = 'Frappe Network Error'

      // rethrow correct error object
      throw new NetworkError(networkErrorMessage, networkErrorCauseInstance)
    }
    handleUnknownError(frappeFunctionError)
    const unknownFrappeErrorMessage = 'unknown frappe error'

    throw new Error(unknownFrappeErrorMessage)
  })

  return functionReturnValue
}

const networkErrorCodesWithUserFacingMessage = [
  networkErrorCodeWrongRequest,
  networkErrorCodeNoPermission,
]

/**
 * Expected/validation-style errors (417/403) carry a safe, specific backend message meant to be
 * read by the user. Unexpected failures (5xx, network errors) may surface raw traceback text
 * instead, so those fall back to the caller's generic message.
 */
const networkErrorMessageOrFallback = (networkError: unknown, fallbackMessage: string): string => {
  if (
    networkError instanceof NetworkError &&
    networkError.cause?.httpErrorCode !== undefined &&
    networkErrorCodesWithUserFacingMessage.includes(networkError.cause.httpErrorCode) &&
    networkError.cause.message
  ) {
    return networkError.cause.message
  }

  return fallbackMessage
}

export {
  type FrappeAppInstance,
  type FrappeAuthInstance,
  type FrappeAuthRequest,
  type FrappeAuthResponse,
  type FrappeCallInstance,
  type FrappeDataInstance,
  type FrappeFileInstance,
  getFrappeAppInstance,
  getFrappeAuthInstance,
  getFrappeCallInstance,
  getFrappeDataInstance,
  getFrappeFileInstance,
  isNetworkErrorEditRedirectErrorPage,
  isNetworkErrorRedirectContentPage,
  NetworkError,
  type NetworkErrorCauseObject,
  networkErrorCodeWrongRequest,
  networkErrorMessageOrFallback,
  transformFrappeErrorObject,
}
